import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import { getUserInfoApiV1UsersMeGet } from "@/api/generated/sdk.gen";
import type { UserInfoResponseSchema } from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Fetch the current signed-in user (DB record incl. is_admin / user_type),
 * for both internal (Azure AD) and external (email-code) users. Used to gate
 * admin-only UI.
 */
export const useMe = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.ME],
    queryFn: async (): Promise<UserInfoResponseSchema> => {
      const response = await getUserInfoApiV1UsersMeGet({ client: apiClient });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch user: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      if (!response.data) {
        throw new Error("User info not found");
      }
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Convenience: is the current user an internal admin? */
export const useIsAdmin = (): boolean => {
  const { data } = useMe();
  return Boolean(data?.user?.is_admin);
};

/**
 * The current user's derived capability set (access levels v1). UI gating only —
 * the backend re-checks per route. `can(cap)` is undefined-safe (returns false
 * while `/me` is loading), so gated UI stays hidden until confirmed allowed.
 */
export const useCapabilities = () => {
  const { data, isLoading } = useMe();
  const capabilities = data?.capabilities ?? [];
  return {
    capabilities,
    isLoading,
    can: (capability: string): boolean => capabilities.includes(capability),
  };
};
